import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  List,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import {
  notificationsService,
  type AppNotification,
} from '../../shared/services/notifications.service';
import { NotificationItem } from '../../shared/components/ui/NotificationItem';

/** Full notification history for the authenticated user. */
export function NotificationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['notifications', 'list', 'page'],
    queryFn: () => notificationsService.list({ size: 50 }),
  });

  const markAll = useMutation({
    mutationFn: notificationsService.markAllRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markOne = useMutation({
    mutationFn: (id: string) => notificationsService.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const openNotification = (n: AppNotification) => {
    if (!n.read) markOne.mutate(n.id);
    if (n.referenceType === 'CONVERSATION') navigate('/messages');
  };

  const items = list.data?.data ?? [];
  const hasUnread = items.some((n) => !n.read);

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, md: 4 } }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ flex: 1 }}>
          {t('notifications.title')}
        </Typography>
        {hasUnread && (
          <Button onClick={() => markAll.mutate()} disabled={markAll.isPending}>
            {t('notifications.markAllRead')}
          </Button>
        )}
      </Stack>

      {list.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, borderRadius: 3, textAlign: 'center' }}>
          <NotificationsNoneRoundedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">{t('notifications.empty')}</Typography>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <List disablePadding>
            {items.map((n) => (
              <NotificationItem key={n.id} notification={n} onClick={openNotification} />
            ))}
          </List>
        </Paper>
      )}
    </Container>
  );
}
